import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMe, useUpdateMe } from "@/hooks/useMe";
import { profileUpdateInputSchema, type ProfileUpdateInput } from "@/schemas";

export default function Profile() {
  const me = useMe();
  const update = useUpdateMe();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const form = useForm<ProfileUpdateInput>({
    resolver: zodResolver(profileUpdateInputSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      job_title: "",
      timezone: "",
    },
  });

  useEffect(() => {
    if (!me.data) return;
    form.reset({
      first_name: me.data.first_name ?? "",
      last_name: me.data.last_name ?? "",
      phone: me.data.phone ?? "",
      job_title: me.data.job_title ?? "",
      timezone: me.data.timezone ?? "",
    });
  }, [me.data, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    await update.mutateAsync(values);
    setSavedAt(Date.now());
  });

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold">Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your contact details. We use these when introducing you to an accountant
          or partner — and on the report when you share your dashboard.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-5 rounded-lg border p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="First name"
            input={
              <input
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("first_name")}
              />
            }
          />
          <Field
            label="Last name"
            input={
              <input
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("last_name")}
              />
            }
          />
        </div>

        <Field
          label="Email"
          hint="Email changes aren't supported yet — coming with the settings work."
          input={
            <input
              readOnly
              disabled
              value={me.data?.email ?? ""}
              className="w-full cursor-not-allowed rounded-md border bg-secondary/40 px-3 py-2 text-muted-foreground"
            />
          }
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Phone"
            input={
              <input
                type="tel"
                placeholder="+44…"
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("phone")}
              />
            }
          />
          <Field
            label="Job title"
            input={
              <input
                placeholder="e.g. Director, Founder"
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("job_title")}
              />
            }
          />
        </div>

        <Field
          label="Timezone"
          hint="Used for compliance deadline reminders. Default: Europe/London."
          input={
            <input
              placeholder="Europe/London"
              className="w-full rounded-md border bg-background px-3 py-2"
              {...form.register("timezone")}
            />
          }
        />

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={update.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
          {savedAt && !update.isPending ? (
            <span className="text-sm text-muted-foreground">Saved.</span>
          ) : null}
          {update.isError ? (
            <span className="text-sm text-destructive">
              {(update.error as Error)?.message ?? "Save failed."}
            </span>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  hint,
  input,
}: {
  label: string;
  hint?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      {input}
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
